import { useEffect, useState } from "react";
import { Heart, Languages } from "lucide-react";
import { http } from "@/lib/api";
import { LANGS } from "@/i18n";

const NAMES = Object.fromEntries(LANGS);

const LangChip = ({ l }) => (
  <span lang={l} data-no-translate style={{ letterSpacing: 0 }} className="rounded-full bg-[#EEF4F0] text-[#2F5548] px-2.5 py-0.5 text-xs">{NAMES[l] || l}</span>
);

const Person = ({ p, i }) => (
  <li className="rounded-2xl bg-white border border-[#1C242B]/10 p-5 flex items-center gap-4 animate-in fade-in slide-in-from-bottom-2" style={{ animationDelay: `${i * 60}ms` }} data-testid={`thanks-person-${i}`}>
    <span className="w-11 h-11 rounded-full bg-[#D96B43]/10 text-[#C05630] grid place-items-center font-display font-bold" data-no-translate>{p.name.slice(0, 1).toUpperCase()}</span>
    <div className="min-w-0">
      <p className="font-display font-semibold text-[#1C242B]" data-no-translate>{p.name}</p>
      <div className="mt-1 flex flex-wrap gap-1.5">{p.langs.map((l) => <LangChip key={l} l={l} />)}</div>
    </div>
    <p className="ml-auto text-right text-sm text-slate-500"><b className="block text-lg text-[#1C242B] font-mono-ui" data-no-translate>{p.accepted}</b>improvements</p>
  </li>
);

export default function Thanks() {
  const [data, setData] = useState(null);
  useEffect(() => { http.get("/translations/thanks").then((r) => setData(r.data)).catch(() => setData({ people: [], languages: [] })); }, []);
  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 pt-12 pb-10" data-testid="thanks-page">
      <p className="overline-label">Our translators</p>
      <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight mt-3">Thank you, volunteers</h1>
      <p className="mt-4 text-base text-slate-600 max-w-2xl">Aptitude Plus is free in 9 languages because volunteers check and improve every translation. These reviewers chose to be credited.</p>
      {data && data.people.length === 0 && <p className="mt-10 text-slate-500" data-testid="thanks-empty">Our first reviewers are getting started. Check back soon.</p>}
      <ul className="mt-10 grid gap-3">{data?.people.map((p, i) => <Person key={`${p.name}-${i}`} p={p} i={i} />)}</ul>
      {data?.languages.length > 0 && (
        <section className="mt-12" data-testid="thanks-languages">
          <h2 className="font-display font-bold text-lg flex items-center gap-2"><Languages size={18} /> Community suggestions by language</h2>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {data.languages.map((l) => (
              <div key={l.lang} className="rounded-2xl bg-white border border-[#1C242B]/10 p-4" data-testid={`thanks-lang-${l.lang}`}>
                <LangChip l={l.lang} />
                <p className="mt-2 text-sm text-slate-600"><b className="font-mono-ui text-[#1C242B]" data-no-translate>{l.accepted}</b> accepted</p>
                <p className="text-xs text-slate-500"><span data-no-translate>{l.suggested}</span> suggested</p>
              </div>
            ))}
          </div>
        </section>
      )}
      <p className="mt-12 text-sm text-slate-500 flex items-center gap-2"><Heart size={14} className="text-[#D96B43]" /> Speak one of our languages? Switch to it and use "Help improve this translation" in the footer.</p>
    </div>
  );
}
